import React, { useState } from "react";
import { API_BASE_URL } from "../../../services/api";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Image,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaImage,
  FaUserCircle,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import WebinarService from "../../../services/webinarService";

const CreateWebinar = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [showSeo, setShowSeo] = useState(false);
  
  // Image states (local preview only, not uploaded yet)
  const [featuredImageFile, setFeaturedImageFile] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState("");
  
  const [instructorImageFile, setInstructorImageFile] = useState(null);
  const [instructorImagePreview, setInstructorImagePreview] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    short_description: "",
    long_description: "",
    instructor_name: "",
    instructor_bio: "",
    webinar_date: "",
    webinar_time: "",
    duration: "2 hours",
    meeting_link: "",
    recording_link: "",
    status: "upcoming",
    seo_title: "",
    seo_description: "",
  });

  // Handle text input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle featured image selection (store locally, don't upload yet)
  const handleFeaturedImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      toast.error("Please upload JPG, PNG, or WEBP image");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB");
      return;
    }

    setFeaturedImageFile(file);
    setFeaturedImagePreview(URL.createObjectURL(file));
  };

  // Handle instructor image selection (store locally, don't upload yet)
  const handleInstructorImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      toast.error("Please upload JPG, PNG, or WEBP image");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB");
      return;
    }

    setInstructorImageFile(file);
    setInstructorImagePreview(URL.createObjectURL(file));
  };

  // Remove featured image
  const removeFeaturedImage = () => {
    setFeaturedImageFile(null);
    setFeaturedImagePreview("");
  };

  // Remove instructor image
  const removeInstructorImage = () => {
    setInstructorImageFile(null);
    setInstructorImagePreview("");
  };

  // Upload image to Cloudinary
  const uploadImageToCloudinary = async (file, type) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    const token = localStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}/admin/upload-webinar-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const data = await response.json();
    if (data.status === true && data.data) {
      return {
        url: data.data.url,
        public_id: data.data.public_id
      };
    } else {
      throw new Error(data.message || "Upload failed");
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title) {
      toast.error("Title is required");
      return;
    }
    if (!formData.instructor_name) {
      toast.error("Instructor name is required");
      return;
    }
    if (!formData.webinar_date) {
      toast.error("Webinar date is required");
      return;
    }
    if (!formData.webinar_time) {
      toast.error("Webinar time is required");
      return;
    }
    if (!formData.status) {
      toast.error("Status is required");
      return;
    }

    setSubmitting(true);
    
    try {
      // Prepare data for submission
      const submitData = { ...formData };
      
      // Upload featured image if selected
      if (featuredImageFile) {
        toast.info("Uploading featured image...");
        const result = await uploadImageToCloudinary(featuredImageFile, 'webinar_featured');
        submitData.featured_image = result.url;
        submitData.featured_image_public_id = result.public_id;
      }
      
      // Upload instructor image if selected
      if (instructorImageFile) {
        toast.info("Uploading instructor image...");
        const result = await uploadImageToCloudinary(instructorImageFile, 'webinar_instructor');
        submitData.instructor_image = result.url;
        submitData.instructor_image_public_id = result.public_id;
      }
      
      // Create webinar
      await WebinarService.adminCreateWebinar(submitData);
      toast.success("Webinar created successfully!");
      navigate("/admin/webinars");
    } catch (error) {
      console.error("Create failed:", error);
      toast.error(error.response?.data?.message || "Failed to create webinar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      <Container fluid className="p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button
              variant="link"
              className="text-decoration-none mb-2 p-0"
              onClick={() => navigate("/admin/webinars")}
            >
              <FaArrowLeft className="me-2" /> Back to Webinars
            </Button>
            <h2 className="mb-0">Create New Webinar</h2>
            <p className="text-muted">Fill in the details below to create a new webinar</p>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={8}>
              {/* Basic Information Section */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">📋 Basic Information</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter webinar title"
                      required
                    />
                    <Form.Text className="text-muted">
                      Slug will be auto-generated from the title
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Short Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="short_description"
                      rows={2}
                      value={formData.short_description}
                      onChange={handleChange}
                      placeholder="Brief description shown on webinar cards"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Long Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="long_description"
                      rows={8}
                      value={formData.long_description}
                      onChange={handleChange}
                      placeholder="Full webinar description. HTML supported: <h3>Heading</h3>, <ul><li>List</li></ul>"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Featured Image</Form.Label>
                    <div className="border rounded p-3 text-center">
                      {featuredImagePreview ? (
                        <div className="mb-3">
                          <Image
                            src={featuredImagePreview}
                            alt="Featured preview"
                            style={{ maxHeight: "200px", maxWidth: "100%" }}
                            className="rounded"
                          />
                          <div className="mt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeFeaturedImage}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <FaImage size={50} className="text-muted mb-2" />
                          <p className="text-muted">No image selected</p>
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFeaturedImageSelect}
                      />
                      <Form.Text className="text-muted">
                        Image will be uploaded when you save the webinar. Max 2MB.
                      </Form.Text>
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* Instructor Information Section */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">👨‍🏫 Instructor Information</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Instructor Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="instructor_name"
                      value={formData.instructor_name}
                      onChange={handleChange}
                      placeholder="Enter instructor name"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Instructor Bio</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="instructor_bio"
                      rows={3}
                      value={formData.instructor_bio}
                      onChange={handleChange}
                      placeholder="Instructor credentials, experience, achievements"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Instructor Image</Form.Label>
                    <div className="border rounded p-3 text-center">
                      {instructorImagePreview ? (
                        <div className="mb-3">
                          <Image
                            src={instructorImagePreview}
                            alt="Instructor preview"
                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                            className="rounded-circle"
                          />
                          <div className="mt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeInstructorImage}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <FaUserCircle size={50} className="text-muted mb-2" />
                          <p className="text-muted">No image selected</p>
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleInstructorImageSelect}
                      />
                      <Form.Text className="text-muted">
                        Image will be uploaded when you save the webinar. Max 2MB.
                      </Form.Text>
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              {/* Schedule & Links Section */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-info text-white">
                  <h5 className="mb-0">📅 Schedule & Links</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Webinar Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="webinar_date"
                      value={formData.webinar_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Webinar Time *</Form.Label>
                    <Form.Control
                      type="time"
                      name="webinar_time"
                      value={formData.webinar_time}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Duration</Form.Label>
                    <Form.Control
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      placeholder="e.g., 2 hours"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Meeting Link</Form.Label>
                    <Form.Control
                      type="url"
                      name="meeting_link"
                      value={formData.meeting_link}
                      onChange={handleChange}
                      placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Recording Link</Form.Label>
                    <Form.Control
                      type="url"
                      name="recording_link"
                      value={formData.recording_link}
                      onChange={handleChange}
                      placeholder="https://youtube.com/... or https://vimeo.com/..."
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Status *</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      required
                    >
                      <option value="upcoming">📅 Upcoming</option>
                      <option value="live">🔴 Live</option>
                      <option value="completed">📹 Completed</option>
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* SEO Section */}
              <Card className="mb-4 shadow-sm">
                <Card.Header 
                  className="bg-secondary text-white"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowSeo(!showSeo)}
                >
                  <h5 className="mb-0">
                    {showSeo ? <FaChevronDown className="me-2" /> : <FaChevronRight className="me-2" />}
                    🔍 SEO Information (Optional)
                  </h5>
                </Card.Header>
                {showSeo && (
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>SEO Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="seo_title"
                        value={formData.seo_title}
                        onChange={handleChange}
                        placeholder="Custom title for search engines"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>SEO Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="seo_description"
                        rows={3}
                        value={formData.seo_description}
                        onChange={handleChange}
                        placeholder="Meta description for search engines (150-160 characters)"
                      />
                    </Form.Group>
                  </Card.Body>
                )}
              </Card>

              {/* Action Buttons */}
              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={submitting}
                    >
                      <FaSave className="me-2" />
                      {submitting ? "Creating..." : "Create Webinar"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      onClick={() => navigate("/admin/webinars")}
                    >
                      <FaTimes className="me-2" /> Cancel
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default CreateWebinar;